import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  useAssignmentWithQuestions, 
  useDeleteAssignmentQuestion,
  useAddAssignmentQuestionWithOptions,
} from "@/hooks/useAssignments";
import { Plus, Trash2, GripVertical, Loader2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AssignmentBuilderProps {
  assignmentId: string;
}

const QUESTION_TYPES = [
  { value: "text", label: "Short Text" },
  { value: "textarea", label: "Long Text" },
  { value: "number", label: "Number" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "multiple_correct", label: "Multiple Correct Answers" },
  { value: "true_false", label: "True/False" },
  { value: "file_upload", label: "File Upload" },
];

interface OptionInput {
  text: string;
  isCorrect: boolean;
}

const needsOptions = (type: string) => 
  ["multiple_choice", "multiple_correct", "true_false"].includes(type);

const getDefaultOptions = (type: string): OptionInput[] => {
  if (type === "true_false") {
    return [
      { text: "True", isCorrect: true },
      { text: "False", isCorrect: false },
    ];
  }
  if (type === "multiple_choice" || type === "multiple_correct") {
    return [
      { text: "", isCorrect: false },
      { text: "", isCorrect: false },
    ];
  }
  return [];
};

export function AssignmentBuilder({ assignmentId }: AssignmentBuilderProps) {
  const { data: assignment, isLoading } = useAssignmentWithQuestions(assignmentId);
  const addQuestion = useAddAssignmentQuestionWithOptions();
  const deleteQuestion = useDeleteAssignmentQuestion();

  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionType, setNewQuestionType] = useState("text");
  const [newQuestionRequired, setNewQuestionRequired] = useState(true);
  const [deleteQuestionId, setDeleteQuestionId] = useState<string | null>(null);
  const [options, setOptions] = useState<OptionInput[]>([]);

  const handleQuestionTypeChange = (type: string) => {
    setNewQuestionType(type);
    setOptions(getDefaultOptions(type));
  };

  const handleOptionTextChange = (index: number, text: string) => {
    setOptions(prev => prev.map((opt, i) => i === index ? { ...opt, text } : opt));
  };

  const handleOptionCorrectChange = (index: number, isCorrect: boolean) => {
    if (newQuestionType === "multiple_choice" || newQuestionType === "true_false") {
      // Only one correct answer allowed
      setOptions(prev => prev.map((opt, i) => ({ ...opt, isCorrect: i === index ? isCorrect : false })));
    } else {
      // Multiple correct answers allowed
      setOptions(prev => prev.map((opt, i) => i === index ? { ...opt, isCorrect } : opt));
    }
  };

  const addOption = () => {
    setOptions(prev => [...prev, { text: "", isCorrect: false }]);
  };

  const removeOption = (index: number) => {
    if (options.length <= 2) return; // Minimum 2 options
    setOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddQuestion = () => {
    if (!newQuestion.trim()) return;
    
    // Validate options for choice questions
    if (needsOptions(newQuestionType)) {
      const validOptions = options.filter(o => o.text.trim());
      if (validOptions.length < 2) {
        return; // Need at least 2 options
      }
      if (!options.some(o => o.isCorrect)) {
        return; // Need at least one correct answer
      }
    }
    
    const nextOrder = (assignment?.questions?.length || 0) + 1;
    
    addQuestion.mutate(
      {
        assignment_id: assignmentId,
        question_text: newQuestion.trim(),
        question_type: newQuestionType,
        is_required: newQuestionRequired,
        sequence_order: nextOrder,
        options: needsOptions(newQuestionType) 
          ? options.filter(o => o.text.trim())
          : undefined,
      },
      {
        onSuccess: () => {
          setNewQuestion("");
          setNewQuestionType("text");
          setNewQuestionRequired(true);
          setOptions([]);
        },
      }
    );
  };

  const canAddQuestion = () => {
    if (!newQuestion.trim()) return false;
    if (needsOptions(newQuestionType)) {
      const validOptions = options.filter(o => o.text.trim());
      if (validOptions.length < 2) return false;
      if (!options.some(o => o.isCorrect)) return false;
    }
    return true;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h5 className="text-sm font-medium">Questions</h5>

      {assignment?.questions?.length === 0 && (
        <p className="text-sm text-muted-foreground">No questions yet. Add your first question below.</p>
      )}

      <div className="space-y-2">
        {assignment?.questions?.map((question, index) => (
          <div
            key={question.id}
            className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30"
          >
            <GripVertical className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {index + 1}. {question.question_text}
                    {question.is_required && <span className="text-destructive ml-1">*</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {QUESTION_TYPES.find(t => t.value === question.question_type)?.label || question.question_type}
                  </p>
                  {question.options && question.options.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {question.options.map((opt) => (
                        <div key={opt.id} className="flex items-center gap-2 text-xs">
                          <span className={opt.is_correct ? "text-green-600 font-medium" : "text-muted-foreground"}>
                            {opt.is_correct ? "✓" : "○"} {opt.option_text}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive flex-shrink-0"
                  onClick={() => setDeleteQuestionId(question.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t pt-4 space-y-3">
        <h5 className="text-sm font-medium">Add Question</h5>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="question-text">Question</Label>
            <Input
              id="question-text"
              placeholder="Enter your question..."
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="question-type">Answer Type</Label>
              <Select value={newQuestionType} onValueChange={handleQuestionTypeChange}>
                <SelectTrigger id="question-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end pb-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="question-required"
                  checked={newQuestionRequired}
                  onCheckedChange={(checked) => setNewQuestionRequired(checked as boolean)}
                />
                <Label htmlFor="question-required" className="text-sm font-normal">
                  Required
                </Label>
              </div>
            </div>
          </div>

          {/* Options for choice questions */}
          {needsOptions(newQuestionType) && (
            <div className="space-y-2 p-3 border rounded-lg bg-muted/20">
              <Label>Answer Options</Label>
              <p className="text-xs text-muted-foreground mb-2">
                {newQuestionType === "multiple_choice" || newQuestionType === "true_false"
                  ? "Select the one correct answer"
                  : "Select all correct answers"}
              </p>
              
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Checkbox
                    checked={option.isCorrect}
                    onCheckedChange={(checked) => handleOptionCorrectChange(index, checked as boolean)}
                  />
                  <Input
                    value={option.text}
                    onChange={(e) => handleOptionTextChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1"
                    disabled={newQuestionType === "true_false"}
                  />
                  {newQuestionType !== "true_false" && options.length > 2 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeOption(index)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              {newQuestionType !== "true_false" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addOption}
                  className="w-full mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Option
                </Button>
              )}
            </div>
          )}

          {/* Info for file upload */}
          {newQuestionType === "file_upload" && (
            <p className="text-xs text-muted-foreground p-3 border rounded-lg bg-muted/20">
              Students will be able to upload files such as Word documents, PDFs, images, etc.
            </p>
          )}

          <Button
            size="sm"
            onClick={handleAddQuestion}
            disabled={!canAddQuestion() || addQuestion.isPending}
          >
            {addQuestion.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Add Question
          </Button>
        </div>
      </div>

      <AlertDialog open={!!deleteQuestionId} onOpenChange={() => setDeleteQuestionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this question. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteQuestionId) {
                  deleteQuestion.mutate({ questionId: deleteQuestionId, assignmentId });
                  setDeleteQuestionId(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
